/**
 * Sunetul mașinii: sinteza pe cilindri din simulatorul vechi (src/audio/engineDsp.js), rulată într-un AudioWorklet
 * și condusă de fizica jocului (turație, sarcină, presiune turbo, viteză, alunecarea roților).
 * Fără AudioWorklet (browsere vechi) rulează pe un ScriptProcessor. AudioContext pornește după o atingere.
 */
import dspSrc from './engineDsp.js?raw';

interface DspLib {
  EngineDSP: new (sr: number) => { setPreset(p: unknown): void; process(l: Float32Array, r: Float32Array, n: number): void; [k: string]: unknown };
  buildPreset(spec: unknown): Record<string, unknown>;
  calibrateGain(p: unknown): void;
}
let lib: DspLib | null = null;
const dsp = (): DspLib => (lib ??= new Function(`${dspSrc}\nreturn { EngineDSP, buildPreset, calibrateGain };`)() as DspLib);

export interface EngineSoundSpec {
  cylinders: number;
  displacementL: number;
  turbo: boolean;
  diesel: boolean;
  carburetor: boolean;
  idleRpm: number;
  redlineRpm: number;
  limiterRpm: number;
  peakRpm: number;
  boreStroke: number;
  body: 'hatch' | 'sedan' | 'suv';
}

/** Presetul de sunet din datele motorului din fișă. */
export function soundPreset(s: EngineSoundSpec): Record<string, unknown> {
  const P = dsp().buildPreset({ cfg: s.cylinders >= 6 ? 'i6' : s.cylinders === 5 ? 'i5' : 'i4', asp: s.turbo ? 't' : 'na', disp: s.displacementL, body: s.body, lvl: 1, bs: s.boreStroke });
  Object.assign(P, { idle: s.idleRpm, limit: s.limiterRpm, redline: s.redlineRpm, peak: s.peakRpm, dial: Math.ceil((s.limiterRpm + 900) / 1000) * 1000, popMul: s.turbo ? 0.6 : 0.3 });
  if (s.diesel) {
    // diesel: ardere mai bruscă (clănțănit), evacuare mai joasă, fără pocnituri
    Object.assign(P, { lp0: Math.round((P.lp0 as number) * 0.7), lp1: Math.round((P.lp1 as number) * 0.55), rasp: 1.3, raspF: 850, jit: 1.4, cylVar: 0.08, raw: 0.03, tickAmt: 0.05, boom: 0.3, boomF: 70, popMul: 0, howl: 0.01 });
  }
  if (s.carburetor) Object.assign(P, { jit: 1.5, cylVar: 0.1, rasp: 1.1, raw: 0.05, lp0: Math.round((P.lp0 as number) * 0.85) });
  dsp().calibrateGain(P);
  return P;
}

const WORKLET = () => `${dspSrc}
class CarProc extends AudioWorkletProcessor {
  constructor() { super(); this.d = new EngineDSP(sampleRate); this.port.onmessage = (e) => applyMsg(this.d, e.data); this.tmp = null; }
  process(inputs, outputs) {
    const out = outputs[0]; if (!out || !out.length || !this.d.P) return true;
    const L = out[0]; let R = out[1];
    if (!R) { if (!this.tmp || this.tmp.length !== L.length) this.tmp = new Float32Array(L.length); R = this.tmp; }
    this.d.process(L, R, L.length);
    return true;
  }
}
const applyMsg = ${apply.toString()};
registerProcessor('car-proc', CarProc);`;

export interface SoundFrame { rpm: number; load: number; throttle: number; boost: number; speed: number; slip: number; running: boolean; impact: number }

function apply(d: Record<string, any>, m: Record<string, any>): void {
  if (m.t === 'preset') { d.setPreset(m.p); d.ex = { rpm: m.p.idle, load: 0.1, boost: 0 }; }
  else if (m.t === 'ext') {
    d.ex = { rpm: m.rpm, load: m.load, boost: m.boost };
    d.setThrottle(m.throttle);
    d.extV = m.speed;
    d.sqT = m.slip;
    d.scrub = 0;
    if (m.running && d.state === 0) d.start();
    else if (!m.running && d.state !== 0) d.stop();
    if (m.impact > 0) d.hit(Math.max(0.2, 1 - m.impact));
  }
}

export class CarSound {
  private ctx: AudioContext;
  private gain: GainNode;
  private node: AudioWorkletNode | ScriptProcessorNode | null = null;
  private local: Record<string, any> | null = null;
  private ready: Promise<void>;
  private volume = 0.8;

  constructor(preset: Record<string, unknown>) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new AC({ latencyHint: 'interactive' });
    this.gain = this.ctx.createGain();
    this.gain.gain.value = this.volume;
    this.gain.connect(this.ctx.destination);
    this.ready = this.init(preset);
  }

  private async init(preset: Record<string, unknown>): Promise<void> {
    try {
      if (!this.ctx.audioWorklet) throw new Error('fără AudioWorklet');
      const url = URL.createObjectURL(new Blob([WORKLET()], { type: 'application/javascript' }));
      await this.ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      const n = new AudioWorkletNode(this.ctx, 'car-proc', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2] });
      n.connect(this.gain);
      n.port.postMessage({ t: 'preset', p: preset });
      this.node = n;
    } catch {
      // varianta veche: procesarea pe firul principal
      const d = new (dsp().EngineDSP)(this.ctx.sampleRate) as unknown as Record<string, any>;
      apply(d, { t: 'preset', p: preset });
      const sp = this.ctx.createScriptProcessor(1024, 0, 2);
      sp.onaudioprocess = (ev) => { const ob = ev.outputBuffer; d.process(ob.getChannelData(0), ob.getChannelData(1), ob.length); };
      sp.connect(this.gain);
      this.local = d;
      this.node = sp;
    }
  }

  resume(): void {
    if (this.ctx.state !== 'running') void this.ctx.resume();
  }

  setVolume(v: number): void {
    this.volume = v;
    this.gain.gain.value = v;
  }

  update(f: SoundFrame): void {
    const m = { t: 'ext', rpm: f.rpm, load: Math.max(0, Math.min(1.2, f.load)), throttle: f.throttle, boost: f.boost, speed: Math.max(0, f.speed), slip: Math.max(0, Math.min(1, (f.slip - 0.06) * 4)), running: f.running, impact: f.impact };
    if (this.local) apply(this.local, m);
    else if (this.node && 'port' in this.node) this.node.port.postMessage(m);
  }

  async dispose(): Promise<void> {
    await this.ready.catch(() => undefined);
    this.node?.disconnect();
    await this.ctx.close();
  }
}
